import {
    Box,
    Button,
    Flex,
    HStack,
    IconButton,
    Image,
    Link as LinkStyle,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalHeader,
    ModalOverlay,
    Select,
    Spacer,
    Stack,
    Table,
    Tag,
    TagLabel,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    useDisclosure,
    useToast
} from "@chakra-ui/react";
import React, { useState } from "react";
import {
    MdAdd,
    MdArrowDropDown,
    MdMoreHoriz,
    MdPlusOne,
    MdSettings
} from "react-icons/md";
import { Link, useHistory, useParams } from "react-router-dom";
import { AccountAvatar } from "../../components/AccountAvatar";
import { Loader } from "../../components/Loader";
import { Title } from "../../components/Title";
import {
    AdvertisementForm,
    AdvertisementPositionLabel,
    ArticleForm,
    IAdvertisementFormValues,
    IArticleFormValues
} from "../../forms";
import {
    AdvertisementsDocument,
    useAdvertisementsQuery,
    useCreateUpdateAdvertisementMutation
} from "../../graphql";
import { formatDateTime } from "../../utils";

const CreateModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onCreate: (values: IAdvertisementFormValues) => void;
}> = ({ isOpen, onClose, onCreate }) => {
    return (
        <Modal
            closeOnOverlayClick={ false }
            isOpen={ isOpen }
            onClose={ onClose }
            size="lg"
        >
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Shto/ndrysho një reklamë</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <AdvertisementForm onSubmit={ onCreate } />
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

const AdvertisementsPage: React.FC = () => {
    const toast = useToast();
    const { isOpen, onOpen, onClose } = useDisclosure();

    const [create] = useCreateUpdateAdvertisementMutation();
    const { data, loading, refetch } = useAdvertisementsQuery();

    const onCreate = async (values: IAdvertisementFormValues) => {
        const { data } = await create({
            variables: {
                mediaKey: values.media?.key,
                isActive: values.isActive === "1",
                position: values.position,
                clickUrl: values.clickUrl
            },
            refetchQueries: [{ query: AdvertisementsDocument }]
        });

        onClose();

        toast({
            description: "Reklama u publikua.",
            status: "success",
            isClosable: true
        });
    };

    return (
        <>
            <CreateModal onClose={ onClose } onCreate={ onCreate } isOpen={ isOpen } />
            <Box
                borderWidth="1px"
                borderRadius="lg"
                overflow="hidden"
                p={ 2 }
                background="white"
            >
                <Title title="Lista e reklamave">
                    <Button
                        leftIcon={ <MdAdd /> }
                        colorScheme="blue"
                        size="sm"
                        variant="ghost"
                        onClick={ onOpen }
                    >
                        Shto / redakto
                    </Button>
                </Title>

                { loading ? (
                    <Loader />
                ) : (
                    <Table variant="striped" size="sm">
                        <Thead>
                            <Tr>
                                <Th></Th>
                                <Th>Pozicioni</Th>
                                <Th>URL</Th>
                                <Th>Statusi</Th>
                                <Th>Autori</Th>
                                <Th isNumeric>Data</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            { data.advertisements.map((item) => (
                                <Tr key={ item.id }>
                                    <Td>
                                        <Image
                                            borderRadius={ 10 }
                                            boxSize="50px"
                                            objectFit="cover"
                                            src={ item.isActive ? item.media.url : "" }
                                        />
                                    </Td>
                                    <Td>{ AdvertisementPositionLabel[item.position] }</Td>
                                    <Td>
                                        { item.clickUrl ? (
                                            <LinkStyle>
                                                <a href={ item.clickUrl } target="_blank">
                                                    { item.clickUrl.slice(0, 30) }...
                                                </a>
                                            </LinkStyle>
                                        ) : (
                                            "Asnjë"
                                        ) }
                                    </Td>
                                    <Td>
                                        { item.isActive ? (
                                            <Tag
                                                size={ "sm" }
                                                borderRadius="full"
                                                variant="solid"
                                                colorScheme="green"
                                            >
                                                <TagLabel>Aktive</TagLabel>
                                            </Tag>
                                        ) : (
                                            <Tag
                                                size={ "sm" }
                                                borderRadius="full"
                                                variant="solid"
                                                colorScheme="red"
                                            >
                                                <TagLabel>Jo aktive</TagLabel>
                                            </Tag>
                                        ) }
                                    </Td>
                                    <Td>
                                        <AccountAvatar
                                            account={ item.author }
                                            role={ false }
                                            size="xs"
                                        />
                                    </Td>
                                    <Td isNumeric>{ formatDateTime(item.createdAt) }</Td>
                                </Tr>
                            )) }
                        </Tbody>
                    </Table>
                ) }
            </Box>
        </>
    );
};

export default AdvertisementsPage;
